import React, { useEffect, useRef } from 'react';
import './CustomCursor.css';

const CustomCursor = () => {
    const canvasRef = useRef(null);
    const cursorRef = useRef(null);

    // Neon Palette (Midnight Orbit Theme)
    const colors = [
        '#00F5FF', // Electric Cyan
        '#FF0095', // Carnival Magenta
        '#FFD700', // Stardust Gold
        '#4E2FB3', // Cosmic Violet
        '#70FF00', // Neon Aurora
    ];

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const cursor = cursorRef.current;

        let width = window.innerWidth;
        let height = window.innerHeight;

        canvas.width = width;
        canvas.height = height;

        let particles = [];
        let mouse = { x: width / 2, y: height / 2 };
        let lastMouse = { x: width / 2, y: height / 2 };

        const onResize = () => {
            width = window.innerWidth;
            height = window.innerHeight;
            canvas.width = width;
            canvas.height = height;
        };

        const onMouseMove = (e) => {
            mouse.x = e.clientX;
            mouse.y = e.clientY;

            // Move custom cursor dot
            if (cursor) {
                cursor.style.transform = `translate(${mouse.x}px, ${mouse.y}px)`;
            }

            // Spawn particles based on distance moved
            const dist = Math.hypot(mouse.x - lastMouse.x, mouse.y - lastMouse.y);
            // Spawn more particles if moving fast
            const count = Math.min(5, Math.floor(dist * 0.5));

            for (let i = 0; i < count; i++) {
                createParticle(mouse.x, mouse.y);
            }

            lastMouse.x = mouse.x;
            lastMouse.y = mouse.y;
        };

        const createParticle = (x, y) => {
            const size = Math.random() * 4 + 2;
            const color = colors[Math.floor(Math.random() * colors.length)];
            const angle = Math.random() * Math.PI * 2;
            const speed = Math.random() * 2 + 0.5;

            particles.push({
                x,
                y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed - 1, // Slight upward burst initially
                gravity: 0.15, // Gravity pulls down
                color,
                size,
                life: 1, // 100% opacity start
                decay: Math.random() * 0.02 + 0.01 // Fade speed
            });
        };

        const animate = () => {
            ctx.clearRect(0, 0, width, height);

            for (let i = 0; i < particles.length; i++) {
                const p = particles[i];

                // Update physics
                p.x += p.vx;
                p.y += p.vy;
                p.vy += p.gravity; // Gravity
                p.life -= p.decay;

                // Draw
                ctx.globalAlpha = p.life;
                ctx.fillStyle = p.color;
                ctx.shadowBlur = 10;
                ctx.shadowColor = p.color;

                ctx.beginPath();
                // Draw confetti shape (rect or circle -> let's do circle/rect mix for variety?)
                // Simple circle for neon glow works best for performance
                ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
                ctx.fill();

                // Remove dead particles
                if (p.life <= 0) {
                    particles.splice(i, 1);
                    i--;
                }
            }

            ctx.globalAlpha = 1;
            requestAnimationFrame(animate);
        };

        window.addEventListener('resize', onResize);
        window.addEventListener('mousemove', onMouseMove);

        animate();

        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('mousemove', onMouseMove);
        };
    }, []);

    return (
        <>
            <canvas
                ref={canvasRef}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    pointerEvents: 'none',
                    zIndex: 9998
                }}
            />
            {/* The main cursor dot */}
            <div
                ref={cursorRef}
                className="custom-cursor-dot"
            />
        </>
    );
};

export default CustomCursor;
